import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, timer } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PaymentService } from '../payment.service';
import { CryptoPayout, CryptoPayoutStatus, CryptoWallet, PaymentSource } from '../payment.type';
import { statusClass } from '../payment-format';
import { PaymentState } from '../payment.state';
import { AuthService } from '../../auth/auth.service';

/**
 * The Payments "Crypto payouts" tab: the hot wallet USDT requests are sent
 * from, the limits it sends under, and every on-chain payout.
 *
 * The wallet's key lives on the server (CRYPTO_PAYOUT_PRIVATE_KEY). It is
 * meant to hold a working float topped up from the main wallet, never the
 * main wallet itself: the server can spend everything in it.
 */
@Component({
  selector: 'app-payment-crypto',
  imports: [
    CommonModule, FormsModule, RouterLink, MatButtonModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatPaginatorModule, MatProgressSpinnerModule, MatSelectModule, MatSlideToggleModule, MatTableModule, MatTooltipModule,
  ],
  templateUrl: './payment-crypto.html',
  styleUrl: './payment-crypto.scss',
})
export class PaymentCrypto implements OnInit {
  private paymentService = inject(PaymentService);
  private snackBar = inject(MatSnackBar);
  private state = inject(PaymentState);
  private destroyRef = inject(DestroyRef);
  readonly canEdit = inject(AuthService).canWrite('suppliers_payments');
  readonly statusClass = statusClass;

  readonly columns = ['created', 'supplier', 'amount', 'status', 'tx', 'actions'];
  readonly statuses: CryptoPayoutStatus[] = ['Queued', 'Signed', 'Confirmed', 'Failed'];

  wallet: CryptoWallet | null = null;
  walletError: string | null = null;
  /** Active Wallet paid-from accounts, for the settings form. */
  sources: PaymentSource[] = [];

  autoPay = false;
  autoPayMax = '';
  dailyLimit = '';
  sourceAccountId = '';
  savingSettings = false;

  payouts: CryptoPayout[] = [];
  total = 0;
  pageIndex = 0;
  pageSize = 20;
  status = '';
  loading = false;
  error: string | null = null;

  private poll?: Subscription;

  ngOnInit(): void {
    this.loadWallet();
    this.loadSettings();
    this.loadPayouts();
    this.paymentService.getPaymentSources(true).subscribe({
      next: s => this.sources = s.filter(x => x.kind === 'Wallet'),
      error: () => this.sources = [],
    });
    this.state.reload$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => { this.loadWallet(); this.loadPayouts(); });
    this.destroyRef.onDestroy(() => this.poll?.unsubscribe());
  }

  loadWallet(): void {
    this.paymentService.getCryptoWallet().subscribe({
      next: w => { this.wallet = w; this.walletError = null; },
      error: err => this.walletError = err?.error?.error ?? 'Failed to load the hot wallet',
    });
  }

  private loadSettings(): void {
    this.paymentService.getCryptoPayoutSettings().subscribe({
      next: s => {
        this.autoPay = s.auto_pay;
        this.autoPayMax = s.auto_pay_max;
        this.dailyLimit = s.daily_limit;
        this.sourceAccountId = s.source_account_id;
      },
      error: err => this.fail(err),
    });
  }

  loadPayouts(): void {
    this.loading = true;
    this.error = null;
    this.paymentService.getCryptoPayouts(this.pageIndex, this.pageSize, this.status).subscribe({
      next: res => {
        this.payouts = res.items;
        this.total = res.total;
        this.loading = false;
        this.pollWhileOpen();
      },
      error: err => { this.error = err?.error?.error ?? 'Failed to load on-chain payouts'; this.loading = false; },
    });
  }

  /** Refreshes on its own while a payout on this page is queued or in flight. */
  private pollWhileOpen(): void {
    this.poll?.unsubscribe();
    if (!this.payouts.some(p => p.status === 'Queued' || p.status === 'Signed')) return;
    this.poll = timer(5000).subscribe(() => { this.loadWallet(); this.loadPayouts(); });
  }

  onPage(e: PageEvent): void {
    this.pageIndex = e.pageIndex;
    this.pageSize = e.pageSize;
    this.loadPayouts();
  }

  onStatus(): void {
    this.pageIndex = 0;
    this.loadPayouts();
  }

  saveSettings(): void {
    this.savingSettings = true;
    this.paymentService.updateCryptoPayoutSettings({
      auto_pay: this.autoPay,
      auto_pay_max: String(this.autoPayMax ?? '').trim(),
      daily_limit: String(this.dailyLimit ?? '').trim(),
      source_account_id: this.sourceAccountId,
    }).subscribe({
      next: () => {
        this.savingSettings = false;
        this.snackBar.open('Crypto payout settings saved', undefined, { duration: 2500 });
        this.loadWallet();
      },
      error: err => { this.savingSettings = false; this.fail(err); },
    });
  }

  cancel(p: CryptoPayout): void {
    this.paymentService.cancelCryptoPayout(p.id).subscribe({
      next: () => { this.snackBar.open('Cancelled — nothing was sent.', undefined, { duration: 3000 }); this.loadPayouts(); },
      error: err => { this.fail(err); this.loadPayouts(); },
    });
  }

  copyAddress(): void {
    if (!this.wallet?.address) return;
    navigator.clipboard?.writeText(this.wallet.address).then(
      () => this.snackBar.open('Address copied', undefined, { duration: 2000 }),
      () => this.snackBar.open('Could not copy; select it instead', undefined, { duration: 3000 }),
    );
  }

  label(p: CryptoPayout): string {
    switch (p.status) {
      case 'Queued': return 'Queued';
      case 'Signed': return 'Sending';
      case 'Confirmed': return p.settlement_id ? 'Sent' : 'Sent, not recorded';
      default: return 'Not sent';
    }
  }

  /** The 24-hour limit used, as a percentage for the meter. */
  get usedPercent(): number {
    const used = +(this.wallet?.sent_last_24h ?? 0);
    const limit = +(this.wallet?.daily_limit ?? 0);
    return limit > 0 ? Math.min(100, (used / limit) * 100) : 100;
  }

  private fail(err: any): void {
    this.snackBar.open(err?.error?.error ?? 'Failed', 'Close', { duration: 8000 });
  }
}
